import React, { useEffect, useState } from 'react';
import deliverabilityService, { DomainHealthDomain, DomainHealthResponse } from '../../../services/deliverabilityService';

const DomainHealthPanel: React.FC = () => {
  const [data, setData] = useState<DomainHealthResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const resp = await deliverabilityService.getDomainHealth({ days: 7, maxDomains: 50 });
        setData(resp);
      } catch (e: any) {
        console.error('Failed to load domain health', e);
        let message = e?.message || 'Failed to load domain health';
        try {
          const parsed = JSON.parse(message);
          if (parsed && typeof parsed === 'object' && parsed.error) {
            message = parsed.error;
          }
        } catch {
          // ignore parse errors and use original message
        }
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const renderStatusPill = (domain: DomainHealthDomain) => {
    const status = domain.status || 'healthy';
    const label = status === 'healthy' ? 'Healthy' : status === 'watch' ? 'Watch' : 'Blocked';
    const classes =
      status === 'healthy'
        ? 'bg-green-100 text-green-800'
        : status === 'watch'
        ? 'bg-yellow-100 text-yellow-800'
        : 'bg-red-100 text-red-800';
    return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${classes}`}>{label}</span>;
  };

  if (loading) {
    return (
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mt-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Domain Health</h3>
        <p className="text-sm text-gray-500">Loading domain health…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mt-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Domain Health</h3>
        <p className="text-sm text-gray-500">
          We couldn&apos;t load domain reputation data yet. This usually just means you haven&apos;t sent any tracked cold emails
          or added a tracking domain. Once you start sending campaigns, we&apos;ll calculate per-domain bounce and complaint
          rates here.
        </p>
      </div>
    );
  }

  if (!data || !data.domains || data.domains.length === 0) {
    return (
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mt-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Domain Health</h3>
        <p className="text-sm text-gray-500">
          No domain health data yet. Connect a sending mailbox and send a small cold email campaign; as soon as we see
          some volume, we&apos;ll start showing per-domain bounce and complaint rates here.
        </p>
      </div>
    );
  }

  const windowCopy = `Last ${data.window.days} days (${new Date(data.window.since).toLocaleDateString()} – ${new Date(
    data.window.until
  ).toLocaleDateString()})`;

  return (
    <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mt-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Domain Health</h3>
          <p className="text-sm text-gray-500">Per-domain bounce and complaint rates across your cold email sends.</p>
          <p className="text-xs text-gray-400 mt-1">{windowCopy}</p>
        </div>
        <div className="text-sm text-gray-600">
          <span className="font-semibold">Total sent:</span> {data.totals.sent.toLocaleString()} ·{' '}
          <span className="font-semibold">Bounce rate:</span> {(data.totals.bounceRate * 100).toFixed(1)}% ·{' '}
          <span className="font-semibold">Complaint rate:</span> {(data.totals.complaintRate * 100).toFixed(2)}%
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Domain</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Sent</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Bounce rate</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Complaint rate</th>
              <th className="px-3 py-2 text-left font-medium text-gray-500">Alerts</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {data.domains.map((d) => (
              <tr key={d.domain}>
                <td className="px-3 py-2 font-medium text-gray-900">{d.domain}</td>
                <td className="px-3 py-2">{renderStatusPill(d)}</td>
                <td className="px-3 py-2 text-gray-700">{d.sent.toLocaleString()}</td>
                <td className="px-3 py-2 text-gray-700">{(d.bounceRate * 100).toFixed(1)}%</td>
                <td className="px-3 py-2 text-gray-700">{(d.complaintRate * 100).toFixed(2)}%</td>
                <td className="px-3 py-2">
                  {d.alerts && d.alerts.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {d.alerts.map((a) => (
                        <span
                          key={a}
                          className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            a.includes('critical')
                              ? 'bg-red-100 text-red-800'
                              : a.includes('warning')
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {a.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">None</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DomainHealthPanel;
